import React, { useContext, useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
    Stack,
    TextField,
    Typography,
    alpha,
} from "@mui/material";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import SubTopHeader from "./menus/SubTopHeader";
import { API_URL } from "./config";
import { UserRoleContext } from "./GetUserRole";

const defaultForm = {
    email: "",
    password: "",
    confirmPassword: "",
    business: "",
    referralCode: "",
};

const AuthPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { code: referralParam } = useParams();
    const [searchParams] = useSearchParams();

    const initialMode = searchParams.get("mode") === "signup" ? "signup" : "login";
    const [mode, setMode] = useState(initialMode);
    const isLogin = mode === "login";

    const [loading, setLoading] = useState(false);
    const [showReset, setShowReset] = useState(false);
    const [formData, setFormData] = useState({ ...defaultForm, referralCode: referralParam || "" });
    const { refreshRole } = useContext(UserRoleContext);
    const [readyToLoginDialogOpen, setReadyToLoginDialogOpen] = useState(false);

    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    useEffect(() => {
        navigate(
            { pathname: location.pathname, search: isLogin ? "" : "?mode=signup" },
            { replace: true }
        );
    }, [isLogin, location.pathname, navigate]);

    const title = useMemo(() => {
        if (showReset) return "Reset your password";
        return isLogin ? "Welcome back" : "Create your account";
    }, [isLogin, showReset]);

    const subtitle = useMemo(() => {
        if (showReset) return "Enter your email and we will send reset instructions.";
        return isLogin
            ? "Sign in to manage QR campaigns, analytics, and business tools."
            : "Get started with dynamic QR, team management, and scan insights.";
    }, [isLogin, showReset]);


    const authFieldSx = {
        "& .MuiOutlinedInput-root": {
            height: 56,
            borderRadius: 1,
            "& input": {
                height: "100%",
                boxSizing: "border-box",
                padding: "0 14px",
                display: "flex",
                alignItems: "center",
            },
        },
    };

    const closeSnackbar = () => setSnackbar((prev) => ({ ...prev, open: false }));

    const onChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetView = (nextMode = mode) => {
        setMode(nextMode);
        setShowReset(false);
        setFormData({ ...defaultForm, referralCode: referralParam || "" });
    };

    const onLoginOrRegister = async () => {
        const formDataObj = new FormData();
        formDataObj.append("action", isLogin ? "login" : "register");
        formDataObj.append("email", formData.email);
        formDataObj.append("password", formData.password);

        if (!isLogin) {
            formDataObj.append("confirmPassword", formData.confirmPassword);
            formDataObj.append("business", formData.business);
            formDataObj.append("referralCode", formData.referralCode);
        }

        const response = await fetch(`${API_URL}auth.php`, {
            method: "POST",
            credentials: "include",
            body: formDataObj,
        });

        if (!response.ok) throw new Error("Failed to connect to the server.");
        return response.json();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const result = await onLoginOrRegister();

            if (result.success && isLogin) {
                refreshRole();
                navigate("/list");
                return;
            }

            if (result.success && !isLogin) {
                setSnackbar({
                    open: true,
                    message: result.message || "Registration successful.",
                    severity: "success",
                });
                setReadyToLoginDialogOpen(true);
                return;
            }

            setSnackbar({
                open: true,
                message: result.message || (isLogin ? "Login failed." : "Registration failed."),
                severity: result.success ? "warning" : "error",
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message || "An error occurred. Please try again.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const formDataObj = new FormData();
            formDataObj.append("action", "resetPassword");
            formDataObj.append("email", formData.email);

            const response = await fetch(`${API_URL}auth.php`, {
                method: "POST",
                credentials: "include",
                body: formDataObj,
            });

            if (!response.ok) throw new Error("Failed to connect to the server.");

            const result = await response.json();
            if (!result.success) {
                setSnackbar({ open: true, message: result.error || "Failed to send reset link.", severity: "error" });
                return;
            }

            setSnackbar({
                open: true,
                message: "Password reset instructions have been sent to your email.",
                severity: "success",
            });
            setShowReset(false);
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.message || "An error occurred. Please try again.",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                px: 2,
                pt: { xs: 11, md: 14 },
                pb: 4,
                display: "grid",
                placeItems: "center",
            }}
        >
            <SubTopHeader />

            <Card
                sx={{
                    width: "100%",
                    maxWidth: 980,
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: alpha("#6271ff", 0.2),
                    background: "linear-gradient(145deg, #ffffff 0%, #f8faff 100%)",
                    boxShadow: "0 24px 60px rgba(31,42,68,0.14)",
                }}
            >
                <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 3, md: 4 }}>
                        <Box
                            sx={{
                                flex: 1,
                                borderRadius: 3,
                                p: { xs: 2.5, md: 3 },
                                background: `radial-gradient(circle at 20% 0%, ${alpha("#7f63fb", 0.2)}, transparent 35%),
                    linear-gradient(135deg, #1f2a44 0%, #2e3f66 100%)`,
                                color: "common.white",
                            }}
                        >
                            <Typography sx={{ fontWeight: 800, fontSize: { xs: "1.5rem", md: "1.8rem" }, mb: 1.5 }}>{title}</Typography>
                            <Typography sx={{ color: alpha("#ffffff", 0.88), mb: 3 }}>{subtitle}</Typography>
                            <Stack spacing={1.2}>
                                {["Responsive dashboard UX", "Secure account workflow", "Quick setup for teams"].map((item) => (
                                    <Typography key={item} sx={{ fontSize: "0.95rem", color: alpha("#fff", 0.9) }}>
                                        • {item}
                                    </Typography>
                                ))}
                            </Stack>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <form onSubmit={showReset ? handleResetSubmit : handleSubmit}>
                                <Stack spacing={1.5}>
                                    {!isLogin && !showReset && (
                                        <>
                                            <TextField label="Business Name" name="business" value={formData.business} onChange={onChange} required fullWidth sx={authFieldSx} />
                                            <TextField label="Referral Code" name="referralCode" value={formData.referralCode} onChange={onChange} fullWidth sx={authFieldSx} />
                                        </>
                                    )}
                                    <TextField label="Email" name="email" type="email" value={formData.email} onChange={onChange} required fullWidth sx={authFieldSx} />
                                    {!showReset && (
                                        <TextField label="Password" name="password" type="password" value={formData.password} onChange={onChange} required fullWidth sx={authFieldSx} />
                                    )}
                                    {!isLogin && !showReset && (
                                        <TextField
                                            placeholder="Confirm Password *"
                                            name="confirmPassword"
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={onChange}
                                            required
                                            fullWidth
                                            sx={authFieldSx}
                                        />
                                    )}

                                    <Button type="submit" variant="contained" disabled={loading} sx={{ mt: 1, borderRadius: 2.5, py: 1.2, fontWeight: 700 }}>
                                        {loading ? <CircularProgress size={22} /> : showReset ? "Send Reset Instructions" : isLogin ? "Login" : "Create Account"}
                                    </Button>
                                </Stack>
                            </form>

                            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} justifyContent="space-between" sx={{ mt: 2 }}>
                                {!showReset ? (
                                    <Button onClick={() => resetView(isLogin ? "signup" : "login")} sx={{ textTransform: "none", fontWeight: 700 }}>
                                        {isLogin ? "Need an account? Sign up" : "Have an account? Login"}
                                    </Button>
                                ) : (
                                    <Button onClick={() => setShowReset(false)} sx={{ textTransform: "none", fontWeight: 700 }}>
                                        Back to login
                                    </Button>
                                )}

                                {isLogin && !showReset && (
                                    <Button onClick={() => setShowReset(true)} sx={{ textTransform: "none", fontWeight: 700 }}>
                                        Forgot password?
                                    </Button>
                                )}
                            </Stack>
                        </Box>
                    </Stack>
                </CardContent>
            </Card>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={closeSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <Dialog
                open={readyToLoginDialogOpen}
                onClose={() => setReadyToLoginDialogOpen(false)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>Registration complete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Your account has been created successfully. Are you ready to login now?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setReadyToLoginDialogOpen(false)}>Not yet</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setReadyToLoginDialogOpen(false);
                            resetView("login");
                            navigate("/auth");
                        }}
                    >
                        Yes, go to login
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AuthPage;
